import React from 'react';
import { UserIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const WorkingThroughConflictStage1 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Scenario 1: Personal conflict (you and someone else)" icon={<UserIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* TL;DR Section */}
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <p className="mb-4">You need to do 4 things:</p>
          <ol className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Meet them where they are</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Focus on the problem, not each other</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Move forwards</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light font-bold mr-2">–</span>
              <span>Reflect</span>
            </li>
          </ol>
          <p className="mt-4">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Introduction */}
      <PlaybookCard>
        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300 mb-2">
            Conflict = disagreement.
          </p>
          <p className="text-gray-300 mb-2">
            …You have one opinion
          </p>
          <p className="text-gray-300 mb-2">
            …Someone else has the polar opposite opinion
          </p>
          <p className="text-gray-300 mb-2">
            We got conflict!
          </p>
          <p className="text-gray-300 mb-2">
            It happens. It's often you&lt;&gt;founder–they think x and you thought y and now you are both at loggerheads. Not good for business. Not good for the relationship. This is how to get past that.
          </p>
          <p className="text-gray-300 font-semibold">
            Let's dive in! 🚀
          </p>
        </div>
      </PlaybookCard>

      {/* Meet them where they are */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Meet them where they are
        </h3>
        <p className="text-gray-300 mb-6">
          Begin by understanding the other person's perspective without judgment or bias. This fosters a non-confrontational environment where both of you <span className="text-white font-bold">‼️ feel heard.</span>
        </p>

        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
          <span className="text-primary-light mr-2">⚡</span>
          Action Step: Start a conversation and listen
        </h4>

        <CopyToNotion title="Message Template" slack={true}>
          <p className="text-gray-300">
            "Hey {'{founder}'}, we seem to be in pretty different places on {'{decision x}'}. Can we discuss f2f – I want to understand where you're coming from so we can align on the best path forward for the business?"
          </p>
        </CopyToNotion>

        <div className="mt-6 mb-6">
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
            <span className="text-primary-light mr-2">⚡</span>
            LISTEN:
          </h4>
          <p className="text-gray-300 mb-4">
            Engage in a conversation where you genuinely listen to the other person's concerns and viewpoints without planning your response.
          </p>

          <p className="text-gray-300 mb-2">My favourite 3 questions to ask:</p>

          <div className="space-y-4 mt-4">
            <div className="p-3 border border-gray-700/50 rounded-lg bg-gray-800/30">
              <p className="text-gray-200 font-medium">What led you to that point of view?</p>
            </div>
            <div className="p-3 border border-gray-700/50 rounded-lg bg-gray-800/30">
              <p className="text-gray-200 font-medium">How does this situation make you feel?</p>
            </div>
            <div className="p-3 border border-gray-700/50 rounded-lg bg-gray-800/30">
              <p className="text-gray-200 font-medium">What do you want to see happen?</p>
            </div>
          </div>
        </div>

        <div className="mt-6 mb-6">
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
            <span className="text-primary-light mr-2">⚡</span>
            EMPATHY:
          </h4>
          <p className="text-gray-300 mb-4">
            Understand their feelings and experiences.
          </p>

          <ol className="list-decimal pl-6 space-y-2 text-gray-300">
            <li>What makes you think that?</li>
            <li>What do you hope happens as a result?</li>
            <li>What assumptions are you making?</li>
            <li>How does x make you feel?</li>
            <li>So if we did x, you are expecting abc benefit, is that right? What makes you confident of that?</li>
            <li>What ideas have you discarded in favour of this approach?</li>
          </ol>

          <p className="text-gray-300 mt-4 mb-4">
            This might involve role-reversal or simply asking thoughtful questions about their perspective:
          </p>

          <p className="text-gray-300 font-medium">
            👉"Let's switch roles. How would you handle it if you were in my shoes?"
          </p>
        </div>

        <div className="mt-6">
          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
            <span className="text-primary-light mr-2">⚡</span>
            ACKNOWLEDGE THEIR FEELINGS AND EXP:
          </h4>
          <p className="text-gray-300 mb-4">
            Validate their emotions and viewpoints, even if you don't agree. A simple acknowledgment can de-escalate a lot of tension.
          </p>

          <div className="space-y-3 text-gray-300 mb-4">
            <p>"I understand that you…</p>
            <p>"I can see that…</p>
            <p>"I hear you…</p>
            <p>"I understand that not being able to see everyone working or feel the vibe of the office has been a struggle for you over the last couple of years…"</p>
            <p>"I can see that it could improve collaboration if we decided to move back to office-based work and i understand you're looking at our Q2 results and looking for levers to pull to improve them."</p>
            <p>"I hear you on the point about {'{x}'} that's something i'm concerned about too."</p>
          </div>
        </div>
      </PlaybookCard>

      {/* Focus on the problem not each other */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Focus on the problem not each other...
        </h3>
        <p className="text-gray-300 mb-6">
          If you approach the conversation early and use the steps above, there's a very good chance you've already done this. It's cathartic to share a problem with another human right! But in some cases, especially where there's a lot of emotion, it's easy for this conversation to turn into personal attacks:
        </p>

        <div className="space-y-3 text-gray-300 mb-6 italic">
          <p>"You can't be serious?"</p>
          <p>"Why do you think that?!"</p>
          <p>"Don't be so fkn naive!"</p>
        </div>

        <p className="text-gray-300 mb-6">
          If you get bombarded by this kinda thing…
        </p>

        <h4 className="text-lg font-semibold text-white mb-3">
          ✅ Do 3 things:
        </h4>

        <ol className="list-decimal pl-6 space-y-2 text-gray-300 mb-6">
          <li>Take a deep breath.</li>
          <li>Pause.</li>
          <li>Then say:</li>
        </ol>

        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300">
            "Let's agree to something….You're not the problem. I'm not the problem…So let's focus on fighting the problem not each other, otherwise we're not going to achieve anything here. Can you agree to that? 
            Good.
            Ok. 
            Let's move forwards."
          </p>
        </div>

        <p className="text-gray-300 mb-4">
          Other phrases that help:
        </p>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">"I understand this is frustrating, but let's see how we can tackle this together."</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">"We both want what's best for the team/business, so let's find a common ground."</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">"We say we're about {'{your_company_core_value}'} I think we should remember that today."</span>
          </li>
        </ul>
      </PlaybookCard>

      {/* Moving forwards */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Moving forwards: finding the path to resolution
        </h3>
        <p className="text-gray-300 mb-6">
          The flow here is basically:
        </p>

        <ol className="list-decimal pl-6 space-y-2 text-gray-300 mb-6">
          <li>Agree on the problem</li>
          <li>Co-create solutions.</li>
          <li>Get to a next step/compromise and commit</li>
        </ol>

        <ExpandableCallout type="info" title="Example scenario" className="mb-6">
          <p className="text-gray-300">
            Founder says "Let's get everyone back in the office" because they believe it'll be good for productivity but you believe this will be terrible for morale and goes against survey data you have where certain functions say their productivity has got a lot better since they worked from home.
          </p>
        </ExpandableCallout>

        <h4 className="text-lg font-semibold text-white mb-3">
          Step 1: Clearly define the conflict
        </h4>
        <div className="mb-6">
          <p className="text-gray-300 font-medium mb-2">
            <span className="text-primary-light mr-2">⚡</span>
            Action Step:
          </p>
          <p className="text-gray-300 mb-4">
            Begin by explicitly stating the conflict without bias based on your understanding in the previous steps.
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">"The primary issue here is a disagreement on the work model that best supports productivity and employee satisfaction. You, as the founder, see value in an office-centric model for better collaboration, while I have concerns based on our team's feedback that suggest a hybrid model fosters better collaboration and job satisfaction. Is that a fair assessment?"</span>
            </li>
          </ul>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">
          Step 2: Co-create solution options
        </h4>
        <div className="mb-6">
          <p className="text-gray-300 font-medium mb-2">
            <span className="text-primary-light mr-2">⚡</span>
            Action Step:
          </p>
          <p className="text-gray-300 mb-4">
            Brainstorm possible solutions that could address both sets of needs.
          </p>

          <ExpandableCallout type="info" title="Facilitation tip" className="mb-4">
            <p className="text-gray-300 mb-2">
              You can do this over a call or you can use a miro board or a whiteboard with a bit of structure. This conflict management canvass = 🔥 as a way to structure it.
            </p>
            <ToolCallout
              tool="default"
              label="Conflict Management Canvas"
              href="https://www.fearlessculture.design/blog-posts/how-to-solve-conflict-with-the-conflict-management-canvas"
              description="A structured canvas to navigate conflict resolution"
            />
          </ExpandableCallout>

          <p className="text-gray-300 mb-4">
            The kind of options you might get to:
          </p>

          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300"><span className="font-medium">Pilot Testing:</span> Propose a trial period for different working models. For example, test a three-month period where the office is required for certain core hours or days of the week, and compare productivity and satisfaction metrics to current levels.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300"><span className="font-medium">Flexible Teams:</span> Suggest allowing teams or departments to choose the model that best suits their work. Monitor and review the impacts on productivity, collaboration, and morale across different teams.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300"><span className="font-medium">Feedback Mechanisms:</span> Implement more robust mechanisms for ongoing feedback during the trial periods, such as regular surveys or a suggestion box to gauge employee sentiment in real time.</span>
            </li>
          </ul>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">
          Step 5: Agree on Evaluation Criteria and Next Steps
        </h4>
        <div className="mb-6">
          <p className="text-gray-300 font-medium mb-2">
            <span className="text-primary-light mr-2">⚡</span>
            Action Step:
          </p>
          <p className="text-gray-300 mb-4">
            Set clear, measurable criteria for evaluating the success of the agreed-upon solution:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">"Let's define what success looks like for each model and agree on the metrics we'll use to evaluate them. These could include performance outputs, employee satisfaction scores, and turnover rates."</span>
            </li>
          </ul>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">
          Step 6: Commit to The Next Step
        </h4>
        <div className="mb-6">
          <p className="text-gray-300 font-medium mb-2">
            <span className="text-primary-light mr-2">⚡</span>
            Action Step:
          </p>
          <p className="text-gray-300 mb-4">
            Determine how you will follow up on the trial and make a long-term decision:
          </p>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3">—</span>
              <span className="text-gray-300">"We can schedule monthly check-ins to review the data and discuss any adjustments needed. At the end of the trial period, we'll have a comprehensive review to decide if we should adjust our working model based on the findings."</span>
            </li>
          </ul>
        </div>
      </PlaybookCard>

      {/* Review and Reflect */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          Review and Reflect
        </h3>

        <h4 className="text-lg font-semibold text-white mb-3">
          <span className="text-primary-light mr-2">⚡</span>
          Action Step: Evaluate and learn from each conflict
        </h4>
        <p className="text-gray-300 mb-6">
          Every conflict, no matter how small, provides a learning opportunity. Reflect on how you handled the situation and what you can improve next time.
        </p>

        <p className="text-gray-300 font-medium mb-2">
          <span className="text-primary-light mr-2">⚡</span>
          REFLECTION:
        </p>
        <p className="text-gray-300 mb-4">
          After resolving a conflict, take some time to reflect on the process:
        </p>

        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">"What did we learn from this?"</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">"How can we handle similar situations better in the future?"</span>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-3">
          ✅ Outcome:
        </h4>
        <ul className="space-y-3">
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">They were heard</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">You were heard</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3">—</span>
            <span className="text-gray-300">You agreed how to move forwards</span>
          </li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default WorkingThroughConflictStage1; 